import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// =============================================================================
// A4.1: CORRELATION ID GENERATOR
// =============================================================================

/**
 * Generate a unique correlation ID for request tracing
 * Format: [timestamp]-[random] e.g., "1707312000000-a1b2c3d4"
 */
const generateCorrelationId = () => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 10);
  return `${timestamp}-${random}`;
};

/**
 * Store last correlation ID for error display
 */
let lastCorrelationId = null;
export const getLastCorrelationId = () => lastCorrelationId;

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// =============================================================================
// REQUEST INTERCEPTOR — Add Correlation ID + Scope Headers
// =============================================================================

api.interceptors.request.use(
  (config) => {
    // A4.1: Generate and attach correlation ID
    const correlationId = generateCorrelationId();
    config.headers['X-Correlation-Id'] = correlationId;
    lastCorrelationId = correlationId;
    
    // Attach Authorization Bearer header from localStorage (Cloudflare/CDN
    // overrides Access-Control-Allow-Origin to wildcard which would break
    // cookie-based auth — Bearer is the durable transport).
    const token = (typeof window !== 'undefined') ? localStorage.getItem('granullo_token') : null;
    if (token && !config.headers['Authorization']) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    
    // Add LIVE scope header for authenticated requests
    if (!config.headers['X-GRANULLO-SCOPE']) {
      config.headers['X-GRANULLO-SCOPE'] = 'LIVE';
    }
    
    // Log outgoing request in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`[API] ${config.method?.toUpperCase()} ${config.url} | CID: ${correlationId}`);
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// =============================================================================
// RESPONSE INTERCEPTOR — Handle Errors with Correlation ID + Token Refresh
// =============================================================================

let isRefreshing = false;
let failedQueue = [];

// Public paths that don't require authentication
const publicPaths = [
  '/login', '/', '/register', '/request-access', '/recover-request', '/recover',
  '/setup-2fa', '/csr/apply', '/csr/invite', '/privacy', '/terms',
  '/scenarios', '/possibilities', '/demo', '/demo/receipts',
  '/ubi-governance', '/regulator-start-here', '/start-here', '/regulators',
  '/regulatory-readiness', '/how-granullo-works', '/model', '/glossary',
  '/help/integrations', '/help/communication', '/onboarding',
  '/dev-settings', '/design-qa', '/internal-docs', '/navigation-audit', '/labs',
  '/design-check', '/trust', '/epistemic-policy', '/invariants', '/activate',
  '/intent-records', '/pricing', '/enterprise', '/preparedness', '/cbdc',
  '/founder', '/start', '/investors', '/lens', '/commit-grants/pool-status',
  '/commit-grants', '/offerings', '/case-studies', '/readiness-check',
  '/what-granullo-is', '/architecture', '/consumer', '/control-room',
  '/ecosystem', '/test-drive', '/join/program-creators', '/join/banks',
  '/join/merchants', '/marketplace',
  '/product', '/platform', '/proof', '/simulate', '/program-builder', '/live-programs',
  '/historical-timeline', '/simulations', '/play', '/observatory',
  '/granullo/demo', '/granullo/observatory', '/granullo/simulator',
  '/granullo/failure-map', '/granullo/oracle', '/granullo/zk-demo',
  '/granullo/simulation',
  '/decision-fork', '/atlas-exposure', '/glass-inspector', '/granullo-atlas',
  '/evidence',
  '/moment',
  '/partner',
  '/workspace',
  '/deploy',
  '/admin/setup',
  '/share',
  '/simulation-room',
  '/demand-pulse',
  '/demo-flow',
  '/ask-intelligence',
  '/data-fabric',
  '/playbook',
  '/signals',
  '/estimator',
  '/partners',
  '/proof',
  '/launch-observability',
  '/gtm',
  '/intelligence',
  '/use-cases',
  '/investor',
  '/trust-room',
  '/play',
  '/programs',
  '/foundations',
  '/proof-loop',
  // The beneficiary's own surface. A farmer has no sponsor session, so a 401
  // here must leave them on their sign-in page rather than bouncing them to
  // the staff login they can never complete.
  '/my-entitlement',
];

const isPublicPath = (path) => publicPaths.some(p =>
  path === p ||
  (p !== '/' && path.startsWith(p + '/')) ||
  (p === '/' && path === '/') ||
  path.startsWith('/v/') ||
  path.startsWith('/verify/') ||
  path.startsWith('/sandbox/') ||
  path.startsWith('/diligence-review/')
);

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => {
    // Capture correlation ID from response if server echoes it
    const serverCorrelationId = response.headers['x-correlation-id'];
    if (serverCorrelationId) {
      lastCorrelationId = serverCorrelationId;
    }
    return response;
  },
  async (error) => {
    // Capture correlation ID from error response
    const serverCorrelationId = error.response?.headers?.['x-correlation-id'];
    if (serverCorrelationId) {
      lastCorrelationId = serverCorrelationId;
    }
    
    // Attach correlation ID to error for downstream handling
    if (error.response) {
      error.correlationId = lastCorrelationId;
    }

    const originalRequest = error.config;

    // 401 handler — distinguish session-expiry from per-endpoint authorization.
    // If we have a Bearer token and the failing endpoint isn't /auth/me, we
    // assume the user's session is fine and a specific endpoint just denied
    // them (RBAC, tenant scope, etc.). DO NOT redirect — let the page handle
    // the empty/forbidden response. Only force-redirect when /auth/me itself
    // returns 401, which is the canonical "session is dead" signal.
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/login') &&
      !originalRequest.url?.includes('/auth/register') &&
      !originalRequest.url?.includes('/auth/refresh')
    ) {
      const isAuthMe = originalRequest.url?.endsWith('/auth/me');
      const hasToken = typeof window !== 'undefined' && !!localStorage.getItem('granullo_token');

      // Per-endpoint 401 with a live token → just return the error, no redirect.
      if (hasToken && !isAuthMe) {
        return Promise.reject(error);
      }

      // /auth/me 401 OR no-token 401 → try refresh, else redirect.
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => {
          return api(originalRequest);
        }).catch(err => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        await api.post('/auth/refresh');
        processQueue(null);
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        if (typeof window !== 'undefined') {
          localStorage.removeItem('granullo_token');
        }
        const currentPath = window.location.pathname;
        if (!isPublicPath(currentPath)) {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    
    // For non-401 errors or 401s already handled, just reject
    return Promise.reject(error);
  }
);

export default api;